import {
  ActionIcon,
  Button,
  Card,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ChevronDown, Clock3, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ChartViewId, CustomTimeSegment } from '../types';
import {
  buildTrendEndOptions,
  buildTrendStartOptions,
  formatSecondLabel,
} from '../utils/trendZoom';
import {
  createCustomSegmentId,
  formatSegmentTabLabel,
  validateCustomSegment,
} from '../utils/customSegment';

type CustomSegmentManagerProps = {
  activeViewId: ChartViewId;
  onActiveViewChange: (viewId: ChartViewId) => void;
  customSegments: CustomTimeSegment[];
  onCustomSegmentsChange: (segments: CustomTimeSegment[]) => void;
  availableSeconds: number[];
  maxAvailableSecond: number;
};

export function CustomSegmentManager({
  activeViewId,
  onActiveViewChange,
  customSegments,
  onCustomSegmentsChange,
  availableSeconds,
  maxAvailableSecond,
}: CustomSegmentManagerProps) {
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [newLabel, setNewLabel] = useState('');
  const [newStart, setNewStart] = useState<string | null>(null);
  const [newEnd, setNewEnd] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const startOptions = useMemo(
    () => buildTrendStartOptions(availableSeconds),
    [availableSeconds]
  );

  const endOptions = useMemo(
    () => buildTrendEndOptions(availableSeconds, newStart),
    [availableSeconds, newStart]
  );

  const resetForm = () => {
    setNewLabel('');
    setNewStart(null);
    setNewEnd(null);
    setValidationError(null);
  };

  const handleCloseModal = () => {
    resetForm();
    closeModal();
  };

  const handleDeleteSegment = (segmentId: string) => {
    onCustomSegmentsChange(
      customSegments.filter(segment => segment.id !== segmentId)
    );
    if (activeViewId === segmentId) {
      onActiveViewChange('full');
    }
  };

  const handleAddSegment = () => {
    if (!newStart || !newEnd) {
      setValidationError('Seleziona inizio e fine del tratto.');
      return;
    }

    const startSecond = Number(newStart);
    const endSecond = Number(newEnd);
    const error = validateCustomSegment(
      startSecond,
      endSecond,
      maxAvailableSecond
    );

    if (error) {
      setValidationError(error);
      return;
    }

    const defaultLabel = `Tratto ${customSegments.length + 1}`;
    const segment: CustomTimeSegment = {
      id: createCustomSegmentId(),
      label: newLabel.trim() || defaultLabel,
      startSecond,
      endSecond,
    };

    onCustomSegmentsChange([...customSegments, segment]);
    onActiveViewChange(segment.id);
    resetForm();
    closeModal();
  };

  return (
    <Stack gap='sm'>
      <Card withBorder padding='sm'>
        <ScrollArea.Autosize mah={120} type='scroll'>
          <Tabs
            value={activeViewId}
            onChange={value => {
              if (value === '__add__') {
                openModal();
                return;
              }
              onActiveViewChange((value as ChartViewId) ?? 'full');
            }}
          >
            <Tabs.List style={{ flexWrap: 'nowrap' }}>
              <Tabs.Tab value='full'>Tratto completo</Tabs.Tab>
              {customSegments.map(segment => (
                <Tabs.Tab
                  key={segment.id}
                  value={segment.id}
                  rightSection={
                    <ActionIcon
                      component='span'
                      variant='subtle'
                      color='red'
                      size='xs'
                      aria-label={`Elimina ${formatSegmentTabLabel(segment)}`}
                      onClick={event => {
                        event.stopPropagation();
                        handleDeleteSegment(segment.id);
                      }}
                    >
                      <Trash2 size={12} />
                    </ActionIcon>
                  }
                >
                  {formatSegmentTabLabel(segment)}
                </Tabs.Tab>
              ))}
              <Tabs.Tab value='__add__'>
                <Group gap={4} wrap='nowrap'>
                  <Plus size={14} />
                  Aggiungi tratto
                </Group>
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
        </ScrollArea.Autosize>
      </Card>

      <Modal
        opened={modalOpened}
        onClose={handleCloseModal}
        title='Nuovo tratto temporale'
        centered
        size='md'
      >
        <Stack gap='sm'>
          <TextInput
            label='Etichetta'
            placeholder={`Tratto ${customSegments.length + 1}`}
            value={newLabel}
            onChange={event => setNewLabel(event.currentTarget.value)}
            variant='filled'
          />
          <Group gap='md' align='flex-end' wrap='wrap'>
            <Select
              label='Inizio'
              placeholder='Seleziona timestamp inizio'
              variant='filled'
              leftSection={<Clock3 size={14} />}
              rightSection={<ChevronDown size={14} />}
              data={startOptions}
              value={newStart}
              onChange={value => {
                setNewStart(value);
                setValidationError(null);
                if (value && newEnd && Number(value) >= Number(newEnd)) {
                  const nextSecond = availableSeconds.find(
                    second => second > Number(value)
                  );
                  setNewEnd(nextSecond != null ? nextSecond.toString() : null);
                }
              }}
              clearable
              w={220}
            />
            <Select
              label='Fine'
              placeholder='Seleziona timestamp fine'
              variant='filled'
              leftSection={<Clock3 size={14} />}
              rightSection={<ChevronDown size={14} />}
              data={endOptions}
              value={newEnd}
              onChange={value => {
                setNewEnd(value);
                setValidationError(null);
              }}
              clearable
              w={220}
            />
          </Group>
          {newStart && newEnd && (
            <Text size='xs' c='dimmed'>
              Intervallo: {formatSecondLabel(Number(newStart))} –{' '}
              {formatSecondLabel(Number(newEnd))}
            </Text>
          )}
          {validationError && (
            <Text size='sm' c='red'>
              {validationError}
            </Text>
          )}
          <Group justify='flex-end' mt='sm'>
            <Button variant='subtle' onClick={handleCloseModal}>
              Annulla
            </Button>
            <Button onClick={handleAddSegment}>Aggiungi</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
